import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  Session as SessionSchema,
  LogSet,
  assembleWorkout,
  bestE1rmByExerciseInWindow,
  lastLoggedKgsFromWorkouts,
  type EquipmentInstanceSurvey,
  type Exercise,
  type KnownLocation,
  type LogSet as LogSetT,
  type Workout,
} from '@jackedlogs/shared';
import type { ProgramApiFetch } from '../ProgramEditor/types';
import {
  clearBestE1rmSnapshot,
  getFallbackBestE1rm,
  hydrateBestE1rmFallback,
  persistBestE1rmSnapshot,
} from './bestE1rmFallback';
import { bestE1rmBySlug, bestE1rmForSlug } from './entryHistoryE1rm';
import {
  instanceSurveyForSlug,
  knownLocationsIn,
  silentInstanceForSlug,
} from './entryHistoryEquipment';
import {
  collectSessions,
  pickHistory,
  type HistoryResult,
  type HistorySession,
} from './entryHistoryPick';

export type { HistorySession, HistoryResult } from './entryHistoryPick';

// One-shot recent-window fetch shared across every entry in the active
// Logger. The HistoryPanel under each entry asks `useEntryHistory(slug)`
// and gets the precomputed pick (last N + best e1RM) or `null` when the
// slug has no past sessions; rendering rule "no history → no panel"
// drops out cheaply. The pure window math lives in ./entryHistoryPick.
//
// Module-scope cache survives Logger modal close-and-reopen within the
// session (1.42.0 ActiveWorkoutContext keeps the modal mounted while
// minimized). `invalidateEntryHistoryCache` is called after every save
// so the next Logger open re-fetches.

const HISTORY_LIMIT = 50;

type CtxValue = {
  ready: boolean;
  getForSlug: (exerciseId: string) => HistoryResult | null;
};

const EntryHistoryContext = createContext<CtxValue | null>(null);

let cachedWorkouts: Workout[] | null = null;
// Single-flight handle used by ensureRecentWorkoutsCached. Co-located
// with cachedWorkouts so invalidateEntryHistoryCache can clear both
// atomically (stale-cache race after concurrent save + plan-start).
let inFlight: Promise<Workout[]> | null = null;
// Bumped by every invalidation. A fetch that started before the bump is
// orphaned: it may still land (nothing cancels it) and its result is
// stale by definition, so it must not be written to the offline snapshot.
let cacheGeneration = 0;
const invalidationSubscribers = new Set<() => void>();

// Called after a workout save so the next Provider read re-fetches the
// fresh window. The Logger Modal stays mounted across visible flips
// (1.46.7 leak fix), which means the Provider's local state needs an
// explicit poke to drop the stale list — the module-scope `cachedWorkouts`
// null on its own can't wake up a mounted Provider.
export function invalidateEntryHistoryCache(): void {
  cachedWorkouts = null;
  cacheGeneration += 1;
  // Also drop any in-flight fetch so a save mid-fetch can't repopulate
  // the cache with the pre-save window — the next ensureRecentWorkoutsCached
  // call would otherwise hit the stale fast-path and serve outdated history
  // on plan-start. Concurrent callers awaiting the orphaned promise still
  // get its (stale) resolution; only fresh callers trigger a re-fetch.
  inFlight = null;
  invalidationSubscribers.forEach((fn) => fn());
}

export function EntryHistoryProvider({
  children,
  catalog,
  apiFetch,
}: {
  children: ReactNode;
  // Needed to rank similar exercises for the no-history estimate. Null
  // during edit flows that opened without a catalog fetch → no estimate.
  catalog: readonly Exercise[] | null;
  /** RW-18: the host's apiFetch, passed straight through to
   *  ensureRecentWorkoutsCached (this module's one network call). Every
   *  OTHER export here is a synchronous reader of the module-scope cache
   *  below and stays parameterless. */
  apiFetch: ProgramApiFetch;
}): React.ReactElement {
  const [workouts, setWorkouts] = useState<Workout[] | null>(cachedWorkouts);

  useEffect(() => {
    const sub = () => setWorkouts(null);
    invalidationSubscribers.add(sub);
    return () => {
      invalidationSubscribers.delete(sub);
    };
  }, []);

  useEffect(() => {
    if (workouts !== null) return;
    let cancelled = false;
    void (async () => {
      // Hydrate the offline snapshot BEFORE the window lands: `ready`
      // flips on setWorkouts below, and TimelineLoggerScreen rebuilds its
      // "% of best" callback off that flag, so a fallback arriving after
      // it would not be picked up until some unrelated re-render.
      // Awaited rather than raced: a failed GET resolves to [] quickly,
      // and losing the race would show no percentage on exactly the cold
      // offline launch this exists for. Runs on every Logger open, not
      // once per launch — the Provider mounts with the modal.
      await hydrateBestE1rmFallback(Date.now());
      // Share the single-flight helper with ensureRecentWorkoutsCached:
      // a warm plan-start primes this Provider's first render with zero
      // extra GET, and an early Provider mount likewise primes a later
      // plan-start. fetchRecentWorkouts swallows errors → empty array,
      // so the catch-all branch is no longer needed here.
      const list = await ensureRecentWorkoutsCached(apiFetch);
      if (!cancelled) setWorkouts(list);
    })();
    return () => {
      cancelled = true;
    };
    // `apiFetch` is host-injected and stable per mount (the RW-18 seam); this
    // effect hydrates the cache once and is driven by `workouts`. Adding the
    // fetcher would re-fire it on every host render. CN-UI-LINT.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workouts]);

  const value = useMemo<CtxValue>(
    () => ({
      ready: workouts !== null,
      // TODO(cleanup): thread the athlete's real unit preference and plate
      // step into `pickHistory`'s 4th arg BEFORE any surface renders
      // `proposals` (EQ-2 fetta 2/3). Left at the default here on purpose —
      // today nothing renders them (features.yaml: absent everywhere) — but
      // the default is kg + the default rung, so the day a panel mounts this
      // it would propose 102,5 to an athlete whose gym goes up by 1.
      getForSlug: (id: string) =>
        workouts ? pickHistory(workouts, id, catalog) : null,
    }),
    [workouts, catalog],
  );

  return createElement(
    EntryHistoryContext.Provider,
    { value },
    children,
  );
}

export function useEntryHistory(exerciseId: string): {
  ready: boolean;
  data: HistoryResult | null;
} {
  const ctx = useContext(EntryHistoryContext);
  if (!ctx) return { ready: false, data: null };
  if (!exerciseId) return { ready: ctx.ready, data: null };
  return { ready: ctx.ready, data: ctx.getForSlug(exerciseId) };
}

// Feature B (prefill): read the most-recent cached session for a slug
// outside React (the add-exercise handler runs above EntryHistoryProvider,
// so it can't use the context). Reads the module-scope window populated on
// Logger open; null when the cache isn't ready or the slug has no history.
export function getCachedHistoryForSlug(
  exerciseId: string,
): HistorySession | null {
  if (!cachedWorkouts || cachedWorkouts.length === 0) return null;
  const sessions = collectSessions(cachedWorkouts, exerciseId);
  return sessions[0] ?? null;
}

// The duration (seconds) of the LAST timed set the athlete actually logged for
// a slug, or null when the window has none. Feeds the hold timer's default
// (TM-13): a countdown seeded from a catalog constant tells the athlete how
// long someone thought the hold should be, while this tells them how long they
// held it last time — and that is the number their own history already knows
// (inbox 08ec8624: the catalog said 20s, his last six sets say 45s).
// Most-recent, not best: a hold default is "what you did", not "your record".
// Reads the same module-scope window as getCachedHistoryForSlug and degrades
// to null on a cold cache, where the caller falls through to the catalog.
export function getLastDurationSecForSlug(exerciseId: string): number | null {
  const session = getCachedHistoryForSlug(exerciseId);
  if (!session) return null;
  // Backwards: the last set of that session is the freshest answer.
  for (let i = session.entry.sets.length - 1; i >= 0; i--) {
    const set = session.entry.sets[i]!;
    const duration = 'duration' in set ? set.duration : undefined;
    if (typeof duration === 'number' && duration > 0) return duration;
  }
  return null;
}

// The last weighted kg the athlete actually logged for a slug, or null when
// the window has none. Twin of getLastDurationSecForSlug on the weight axis,
// and it delegates the "last weighted set" walk to the shared
// lastLoggedKgsFromWorkouts rather than re-rolling it — that function already
// owns the semantics (most recent entry, walk its sets back-to-front, skip
// bodyweight/cardio, a failed set still counts) and a second copy here is
// exactly how the plan-start prefill and the swap prefill would start
// disagreeing about "the weight I ended on last time".
//
// EQ-1-SWAP-PCTMAX: this is step 1 of the swap chain. It is preferred over the
// MAX conversion because it is anchored to a load the athlete really moved on
// THIS exercise, so it carries none of the two-MAX error the conversion does.
export function getLastLoggedKgForSlug(exerciseId: string): number | null {
  if (!cachedWorkouts || cachedWorkouts.length === 0) return null;
  return lastLoggedKgsFromWorkouts(cachedWorkouts, [exerciseId]).get(
    exerciseId,
  ) ?? null;
}

// Cache-bound readers over ./entryHistoryEquipment. The rules they encode —
// what silence may attach, why a place orders instead of filtering, why the
// untagged Default is never displaced — live there with the functions; these
// three only supply the module-scope window.

export function getInstanceSurveyForSlug(
  exerciseId: string,
  /** Where we are now, when known: it RANKS the list, it never trims it. */
  orderByLocation?: string,
): EquipmentInstanceSurvey {
  return instanceSurveyForSlug(cachedWorkouts, exerciseId, orderByLocation);
}

/** Places this athlete has trained at, most recent first. Same warm window. */
export function getKnownLocations(): KnownLocation[] {
  return knownLocationsIn(cachedWorkouts);
}

export function getSilentInstanceForSlug(
  exerciseId: string,
  /** Values declared absent HERE, already resolved. ⛔ Still never a place. */
  absentHere: readonly string[] = [],
  /** Where the entry is being born, when the caller knows it (EQ-1-LASTHERE).
   *  Selects WHETHER silence may speak; never shrinks the list. */
  here: string | null = null,
  /** EQ-1-PIN-DEFAULT — the athlete's elected winner here, when they made one.
   *  `undefined` (the default) keeps the pre-pin behaviour exactly. */
  pinned?: string | null,
): string | null {
  return silentInstanceForSlug(
    cachedWorkouts,
    exerciseId,
    absentHere,
    here,
    pinned,
  );
}

// Best e1RM (kg) across the cached recent-history window for a slug — the
// cache-bound reader over ./entryHistoryE1rm, used by the timeline minicards
// (slice 5c) to show how a just-logged set compares to recent capacity.
// Reads the same module-scope window as getCachedHistoryForSlug; the
// in-progress (unsaved) workout is NOT in it.
export function getBestE1rmForSlug(exerciseId: string): number | null {
  // Cold window (fresh launch, or a GET that failed and degraded to []):
  // fall back to the last snapshot persisted while online, so the
  // percentage still renders offline instead of silently vanishing
  // (LG-20). Only here — with a live window in hand the online answer is
  // authoritative and this path is not consulted at all.
  if (!cachedWorkouts || cachedWorkouts.length === 0) {
    return getFallbackBestE1rm(exerciseId);
  }
  return bestE1rmForSlug(cachedWorkouts, exerciseId);
}

// Snapshot the best qualifying e1RM per exercise over the recent
// corroboration window from the module-scope cache, OUTSIDE React. Read
// at SAVE time (before invalidateEntryHistoryCache wipes the cache) so the
// post-save MAX-regression gate sees the pre-save window — i.e. the recent
// PAST sessions, never the just-saved one (it isn't in the cache yet).
// Empty map when the cache is cold → the gate degrades to legacy
// single-session behaviour. Window default = MAX_REGRESSION_RECENT_WEEKS.
export function snapshotRecentBestE1rm(nowMs: number): Record<string, number> {
  if (!cachedWorkouts || cachedWorkouts.length === 0) return {};
  return bestE1rmByExerciseInWindow({ workouts: cachedWorkouts, nowMs });
}

// Shared single-flight + cache for the recent-sessions fetch. Plan-start
// (PlanScreen / _ProgramsSection) calls this before buildPlanStarter so
// the lastLoggedKgs map is populated whether the Logger was opened in
// this session or not. The same cache feeds EntryHistoryProvider, so a
// warm plan-start primes the Logger's HistoryPanel with zero extra GET.
// Whether the last completed fetch actually reached the server. The
// function below reports both "the athlete has no recent sessions" and
// "the GET failed" as `[]`, which is right for the in-memory cache but
// NOT for the persisted snapshot: an empty answer the server really gave
// should retire a stale best, while a failed one must leave it alone.
let lastFetchReachedServer = false;

async function fetchRecentWorkouts(apiFetch: ProgramApiFetch): Promise<Workout[]> {
  try {
    type Envelope = { session?: unknown; sets?: unknown };
    type Resp = { items?: Envelope[] };
    const res = await apiFetch<Resp>(
      `/api/sessions?include=sets&limit=${HISTORY_LIMIT}`,
    );
    const list: Workout[] = [];
    for (const raw of res.items ?? []) {
      const sParsed = SessionSchema.safeParse(raw.session);
      if (!sParsed.success) continue;
      const setsRaw = Array.isArray(raw.sets) ? raw.sets : [];
      const sets: LogSetT[] = [];
      for (const sr of setsRaw) {
        const lp = LogSet.safeParse(sr);
        if (lp.success) sets.push(lp.data);
      }
      list.push(assembleWorkout(sParsed.data, sets));
    }
    lastFetchReachedServer = true;
    return list;
  } catch {
    lastFetchReachedServer = false;
    return [];
  }
}

// Ensure the recent-sessions cache is populated and return it. Single-
// flight: parallel callers share the same fetch promise (whichever caller
// gets here first supplies the apiFetch used — every host has exactly one
// functionally-equivalent implementation, so this is safe); the module-
// scope cache survives across calls so a subsequent EntryHistoryProvider
// mount reuses it. Returns [] on fetch error (treat-as-no-history).
//
// RW-18: `apiFetch` is a real parameter (not read off a host-set module
// variable) because this is called from OUTSIDE the Logger tree too —
// mobile's ExercisePicker.tsx and useWorkoutStarter.ts warm the recency
// cache before the Logger (or EntryHistoryProvider) ever mounts.
export async function ensureRecentWorkoutsCached(
  apiFetch: ProgramApiFetch,
): Promise<Workout[]> {
  if (cachedWorkouts !== null) return cachedWorkouts;
  if (inFlight === null) {
    const startedGeneration = cacheGeneration;
    inFlight = fetchRecentWorkouts(apiFetch).then((list) => {
      cachedWorkouts = list;
      inFlight = null;
      // Everything below is best-effort maintenance of the OFFLINE
      // snapshot. It is wrapped because this callback used to be pure
      // assignment and could not throw; the only awaiter of this promise
      // is the Provider's unguarded async effect, so a throw here would
      // become an unhandled rejection AND leave the HistoryPanel stuck
      // not-ready.
      try {
        if (startedGeneration !== cacheGeneration) {
          // A save invalidated the cache while this fetch was in the air.
          // The stale in-memory assignment above is a pre-existing,
          // documented race (see invalidateEntryHistoryCache); writing
          // that same stale window to DISK is not — it would outlive the
          // process and under-report a PR set for weeks. Skip it and let
          // the next fetch write the truth.
        } else if (!lastFetchReachedServer) {
          // Failed GET. Leave whatever is on disk alone: it is the only
          // thing an offline launch has to show.
        } else if (list.length === 0) {
          // The server answered, and the answer is "no recent sessions".
          // A definitive empty is not the same as no answer — retire the
          // snapshot instead of showing a percentage for history that no
          // longer exists.
          void clearBestE1rmSnapshot(Date.now());
        } else {
          void persistBestE1rmSnapshot(bestE1rmBySlug(list), Date.now());
        }
      } catch {
        // Snapshot maintenance never breaks the fetch it rides on.
      }
      return list;
    });
  }
  return inFlight;
}
